/**
 * Six lines of DOM helper, deliberately not a framework.
 *
 * The tracker ships a small keyed vdom because its screens re-render constantly.
 * This page renders once and then only swaps a handful of text nodes, so a
 * reconciler here would be machinery with nothing to do.
 */

/**
 * Creates an element.
 *
 * `props` sets properties, except for `class`, `style` and anything starting
 * with `data-`/`aria-`, which are attributes — the distinction matters because
 * `el.class = x` silently does nothing.
 */
export function h(tag, props = {}, ...children) {
  const el = document.createElement(tag);

  for (const [key, value] of Object.entries(props)) {
    if (value === null || value === undefined || value === false) continue;
    if (key === 'class') el.className = value;
    else if (key === 'style') el.setAttribute('style', value);
    else if (key.startsWith('data-') || key.startsWith('aria-') || key === 'role') {
      el.setAttribute(key, value === true ? '' : String(value));
    } else if (key.startsWith('on') && typeof value === 'function') {
      el.addEventListener(key.slice(2).toLowerCase(), value);
    } else {
      el[key] = value;
    }
  }

  for (const child of children.flat(Infinity)) {
    if (child === null || child === undefined || child === false) continue;
    el.append(child instanceof Node ? child : document.createTextNode(String(child)));
  }
  return el;
}

/** Replaces an element's children in one go. */
export function fill(el, ...children) {
  el.replaceChildren(...children.flat(Infinity).filter(c => c !== null && c !== undefined && c !== false));
  return el;
}

export const $ = (selector, root = document) => root.querySelector(selector);

/**
 * Formats an integer with the thin spaces the design uses ("2 480").
 *
 * `Intl` would do this, but its group separator for pl-PL is a non-breaking
 * space that some fonts render as a visible box in the mono face used here.
 */
export function groupInt(value) {
  return String(Math.round(value)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}
